import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Loading, SearchInput } from '@evertrue/et-components';
import { FlexTable } from '@evertrue/sdk';
import classNames from 'classnames';
import EnrichmentModalHeader from '../enrichment-modal-header';
import CollapsibleSection from './collapsible-section';
import { transformData } from 'hooks/use-donor-search-table';
import {
  charitableColumns,
  politicalColumns,
  crowdfundingColumns,
  realEstateColumns,
  secColumns,
} from './table-columns';

const SEARCHABLE_FIELDS = {
  charitable: ['listed_donor', 'recipient', 'recipient_location', 'gift_type', 'year', 'low_amount', 'high_amount'],
  political: [
    'listed_donor',
    'recipient',
    'donor_location',
    'gift_type',
    'year',
    'category',
    'low_amount',
    'high_amount',
  ],
  crowdfunding: ['listed_donor', 'recipient', 'donor_location', 'year', 'low_amount', 'high_amount'],
  realEstate: ['name', 'address_street', 'address_city_state', 'property_type', 'estimated_value'],
  sec: ['name', 'company', 'total_shares_held', 'market_value', 'total_shares_sold'],
};

const normalize = (str) => String(str).toLowerCase().replace(/[$,]/g, '').trim();

const filterRows = (rows, query, fields) => {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return rows;
  return rows.filter((row) => fields.some((field) => normalize(row[field] ?? '').includes(normalizedQuery)));
};

const SEARCH_PLACEHOLDER = {
  charitable: 'Search by donor, recipient, location, gift type, year, or amount',
  political: 'Search by donor, recipient, location, gift type, year, category, or amount',
  crowdfunding: 'Search by donor, recipient, location, year, or amount',
  realEstate: 'Search by name, address, city/state, property type, or value',
  sec: 'Search by name, company, shares, or market value',
};

const TABS = [
  { key: 'charitable', label: 'CHARITABLE' },
  { key: 'political', label: 'POLITICAL' },
  { key: 'crowdfunding', label: 'CROWDFUNDING' },
  { key: 'realEstate', label: 'REAL ESTATE' },
  { key: 'sec', label: 'SEC' },
];

const TAB_COLUMNS = {
  charitable: charitableColumns,
  political: politicalColumns,
  crowdfunding: crowdfundingColumns,
  realEstate: realEstateColumns,
  sec: secColumns,
};

/**
 * Modal for viewing detailed philanthropic and financial signal data.
 * Displays data in a tabbed interface with category-specific tables.
 * Fetches data from the DonorSearch Table API when opened.
 */
const PhilanthropicSignalsModal = ({
  isOpen,
  closeModal,
  contactName = '',
  tableHookResult = { data: { high: [], low: [] }, loading: false, error: null },
  initialTab = 'charitable',
  initialSection = 'highQuality',
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeSection, setActiveSection] = useState(initialSection);
  const [searchText, setSearchText] = useState('');
  const [highPagination, setHighPagination] = useState({ page: 0, pageSize: 10 });
  const [lowPagination, setLowPagination] = useState({ page: 0, pageSize: 10 });

  // Derive tab-specific data from the shared hook result
  const { rawResponse, loading } = tableHookResult;
  const { high, low } = useMemo(() => transformData(rawResponse, activeTab), [rawResponse, activeTab]);
  const filteredHigh = useMemo(
    () => filterRows(high, searchText, SEARCHABLE_FIELDS[activeTab]),
    [high, searchText, activeTab]
  );
  const filteredLow = useMemo(
    () => filterRows(low, searchText, SEARCHABLE_FIELDS[activeTab]),
    [low, searchText, activeTab]
  );

  // Reset to initial tab and section when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      const tabData = transformData(rawResponse, initialTab);
      const section =
        initialSection === 'highQuality' && tabData.high.length === 0 && tabData.low.length > 0
          ? 'lowConfidence'
          : initialSection;
      setActiveSection(section);
      setSearchText('');
    }
  }, [isOpen, initialTab, initialSection, rawResponse]);

  // Reset pagination to page 0 when search text or tab changes
  useEffect(() => {
    setHighPagination((prev) => ({ ...prev, page: 0 }));
    setLowPagination((prev) => ({ ...prev, page: 0 }));
  }, [searchText, activeTab]);

  const handleTabChange = (tabKey) => {
    const tabData = transformData(rawResponse, tabKey);
    const section = tabData.high.length === 0 && tabData.low.length > 0 ? 'lowConfidence' : 'highQuality';
    setActiveTab(tabKey);
    setActiveSection(section);
    setSearchText('');
  };

  const columns = TAB_COLUMNS[activeTab] || [];

  const renderContent = () => {
    if (loading) {
      return <Loading />;
    }

    const noResults = filteredHigh.length === 0 && filteredLow.length === 0;

    return (
      <>
        {noResults && <div className="philanthropic-signals-modal--empty">No records found</div>}
        <CollapsibleSection
          key={`high-${activeTab}-${activeSection}`}
          title="High Confidence Matches"
          count={filteredHigh.length}
          defaultExpanded={activeSection === 'highQuality'}
        >
          {filteredHigh.length > 0 ? (
            <FlexTable
              tableKey={`${activeTab}-high-table`}
              columns={columns}
              rows={filteredHigh}
              paginationMode="client"
              sortingMode="client"
              pageSizeOptions={[5, 10, 25]}
              paginationModel={highPagination}
              onPaginationModelChange={setHighPagination}
              getRowHeight={() => 'auto'}
              hideFooter={filteredHigh.length <= highPagination.pageSize}
              density="compact"
              showToolbar={false}
              showCellVerticalBorder={false}
              showColumnVerticalBorder={false}
            />
          ) : (
            <div className="philanthropic-signals-modal--empty">No high confidence matches</div>
          )}
        </CollapsibleSection>
        <CollapsibleSection
          key={`low-${activeTab}-${activeSection}`}
          title="Low Confidence Matches"
          count={filteredLow.length}
          defaultExpanded={activeSection === 'lowConfidence'}
        >
          {filteredLow.length > 0 ? (
            <FlexTable
              tableKey={`${activeTab}-low-table`}
              columns={columns}
              rows={filteredLow}
              paginationMode="client"
              sortingMode="client"
              pageSizeOptions={[5, 10, 25]}
              paginationModel={lowPagination}
              onPaginationModelChange={setLowPagination}
              getRowHeight={() => 'auto'}
              hideFooter={filteredLow.length <= lowPagination.pageSize}
              density="compact"
              showToolbar={false}
              showCellVerticalBorder={false}
              showColumnVerticalBorder={false}
            />
          ) : (
            <div className="philanthropic-signals-modal--empty">No low confidence matches</div>
          )}
        </CollapsibleSection>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} size="1200px">
      <EnrichmentModalHeader
        headerText={`${contactName}'s Philanthropic and Financial Signals`}
        closeModal={closeModal}
      />

      <div className="philanthropic-signals-modal">
        <div className="philanthropic-signals-modal--tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tab.key === activeTab}
              aria-controls={`tabpanel-${tab.key}`}
              className={classNames('philanthropic-signals-modal--tab', {
                'philanthropic-signals-modal--tab-active': tab.key === activeTab,
              })}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="philanthropic-signals-modal--search">
          <SearchInput value={searchText} onChange={setSearchText} placeholder={SEARCH_PLACEHOLDER[activeTab]} />
        </div>

        <div className="philanthropic-signals-modal--content" role="tabpanel" id={`tabpanel-${activeTab}`}>
          {renderContent()}
        </div>
      </div>
    </Modal>
  );
};

PhilanthropicSignalsModal.propTypes = {
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  contactName: PropTypes.string,
  /** Shared hook result from useDonorSearchTable (called in parent) */
  tableHookResult: PropTypes.shape({
    rawResponse: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.object,
  }),
  initialTab: PropTypes.oneOf(['charitable', 'political', 'crowdfunding', 'realEstate', 'sec']),
  initialSection: PropTypes.oneOf(['highQuality', 'lowConfidence']),
};

export default PhilanthropicSignalsModal;
